import { AppData, Lightning } from "@lightningjs/sdk";
import { AppSetup } from "../../../app-setup";
import { Helper } from "../../../helper";

const TEXT_X = 205;
const PROGRESS_X = TEXT_X; // aligns with ProgramName in unfocused rows
const PROGRESS_W = 850;
const PROGRESS_Y = 75;

export const ITEM_H = 102;
const CARD_H = 245;
const CARD_MARGIN = 34;
export const FOCUSED_H = CARD_H + CARD_MARGIN * 2; // 313

const V_IN_CARD = Math.round((CARD_H - ITEM_H) / 2);   // 71
const CONTENT_Y_FOCUSED = CARD_MARGIN + V_IN_CARD;      // 105
const NOW_PLAYING_Y = CONTENT_Y_FOCUSED + PROGRESS_Y;   // 180

const CHANNEL_NAME_TEXT_FOCUSED = {
    fontSize: 29, fontWeight: 900, textColor: 0xffffffff,
    wordWrapWidth: 850, letterSpacing: 0.58, maxLines: 0, textOverflow: ''
};

const CHANNEL_NAME_TEXT_UNFOCUSED = {
    fontSize: 28, fontWeight: 700, textColor: 0xffffffff,
    wordWrapWidth: 850, letterSpacing: 0, maxLines: 1, textOverflow: 'ellipsis'
};

export default class ChannelListItem extends Lightning.Component {

    private _itemIndex = 0;
    private _focusColor: number | null = null;
    private _iconUrl = '';

    private get _s() { return this as any; }

    static override _template(): Lightning.Component.Template<Lightning.Component.TemplateSpecLoose> {
        return {
            w: 1055,
            h: ITEM_H,
            collision: true,
            FocusBg: {
                x: 41, // 61 (Icon x) - 20 (Target margin)
                y: CARD_MARGIN,
                w: 1037, // 1078 - 41
                h: CARD_H,
                rect: true,
                color: 0xe6000000,
                shader: {
                    type: Lightning.shaders.RoundedRectangle,
                    radius: 20,
                    stroke: 0,
                    strokeColor: 0xffffffff
                },
                visible: false
            },
            ChannelNumber: {
                x: 32,
                mountX: 1,
                y: 51,
                mountY: 0.5,
                text: {
                    text: '',
                    fontSize: 29,
                    fontWeight: 500,
                    textColor: 0x80ffffff,
                    letterSpacing: 0.58
                }
            },
            Icon: {
                x: 76,
                y: 0,
                w: 102,
                h: 102,
                rect: true,
                color: 0x80ffffff,
                shader: { type: Lightning.shaders.RoundedRectangle, radius: 10 },
                Logo: {
                    x: 10,
                    y: 10,
                    w: 82,
                    h: 82,
                    visible: false
                }
            },
            ChannelName: {
                x: TEXT_X,
                y: 10,
                visible: false,
                text: {
                    text: '',
                    fontSize: 20,
                    fontFace: Helper.getFontFamily('Black'),
                    textColor: 0xffffffff,
                    wordWrapWidth: 850,
                    maxLines: 1,
                    textOverflow: 'ellipsis'
                }
            },
            ProgramName: {
                x: TEXT_X,
                y: 30,
                w: 850,
                h: 38,
                text: {
                    text: '',
                    fontSize: 24,
                    textColor: 0xb3ffffff,
                    wordWrapWidth: 850,
                    maxLines: 1,
                    textOverflow: 'ellipsis'
                }
            },
            ProgressTrack: {
                x: PROGRESS_X,
                y: PROGRESS_Y,
                w: PROGRESS_W,
                h: 3,
                rect: true,
                color: 0x80ffffff,
                ProgressFill: {
                    x: 0,
                    y: 0,
                    w: 0,
                    h: 3,
                    rect: true,
                    color: 0xffffffff
                }
            },
            // Shown only in focused state — replaces the progress bar
            NowPlayingWrapper: {
                x: PROGRESS_X,
                y: PROGRESS_Y,
                w: PROGRESS_W,
                visible: false,
                flex: { direction: 'row', alignItems: 'flex-start' },
                Prefix: {
                    text: {
                        text: 'Now Playing: ',
                        fontSize: 24,
                        fontWeight: 500,
                        lineHeight: 34,
                        textColor: 0xff30cf58
                    }
                },
                Title: {
                    text: {
                        text: '',
                        fontSize: 24,
                        fontWeight: 500,
                        lineHeight: 34,
                        textColor: 0xffffffff,
                        wordWrapWidth: 650,
                        maxLines: 2,
                        textOverflow: 'ellipsis'
                    }
                }
            }
        }
    }

    static get __componentName(): string {
        return 'ChannelListItem'
    }

    set itemIndex(val: number) {
        this._itemIndex = val;
        this._s.tag('ChannelNumber').text.text = (val + 1).toString();
    }

    set iconUrl(url: string) {
        this._iconUrl = url;
    }

    set channelNumber(val: string) {
        this._s.tag('ChannelNumber').text.text = val;
    }

    set channelName(val: string) {
        this._s.tag('ChannelName').text.text = val;
    }

    set programName(val: string) {
        this._s.tag('ProgramName').text.text = val;
        this._s.tag('NowPlayingWrapper.Title').text.text = val;
    }

    set progress(val: number) {
        this._s.tag('ProgressTrack.ProgressFill').w = Math.round(Math.max(0, Math.min(1, val)) * PROGRESS_W);
    }

    override _init() {
        const { appState } = AppData as AppSetup;
        this._focusColor = Helper.getHexaColor(
            appState?.getAppApiConfig()?.appLayoutConfig?.textfocuscolor
        ) || 0xffffffff;
    }

    override _enable() {
        if (this._iconUrl) {
            this._s.tag('Icon.Logo').patch({
                visible: true,
                texture: {
                    type: Lightning.textures.ImageTexture,
                    src: this._iconUrl,
                }
            });
        }
    }

    override _focus() {
        const s = this._s;
        this.h = FOCUSED_H;
        s.tag('FocusBg').patch({ visible: true, shader: { stroke: 7, strokeColor: this._focusColor } });
        s.tag('Icon').patch({ w: 134, h: 134, x: 61, y: CONTENT_Y_FOCUSED - 10, color: this._focusColor });
        s.tag('Icon.Logo').patch({ w: 114, h: 114 });
        s.tag('ChannelNumber').patch({ visible: true, x: 34, y: CONTENT_Y_FOCUSED + 57, text: { fontWeight: 700, textColor: 0xffffffff, fontFace: Helper.getFontFamily('Black') } });
        s.tag('ChannelName').patch({ visible: true, y: CONTENT_Y_FOCUSED - 10, text: CHANNEL_NAME_TEXT_FOCUSED });
        s.tag('ProgramName').visible = false;
        s.tag('ProgressTrack').visible = false;
        s.tag('NowPlayingWrapper').patch({ y: NOW_PLAYING_Y, visible: true });
        s.fireAncestors('$channelItemFocused', this._itemIndex);
        (this as any).fireAncestors('$itemChanged');
    }

    override _handleDown() {
        (this as any).fireAncestors('$navigateChannelDown');
        return true;
    }

    override _handleUp() {
        (this as any).fireAncestors('$navigateChannelUp');
        return true;
    }

    _handleScroll(obj: any) {
        const signal = (obj?.wheelDeltaY > 0) ? '$navigateChannelUp' : '$navigateChannelDown';
        (this as any).fireAncestors(signal);
        return true;
    }

    override _handleEnter() {
        this._s.fireAncestors('$channelItemEnter', this._itemIndex);
        return true;
    }

    _handleClick() {
        this._s.fireAncestors('$channelItemEnter', this._itemIndex);
        return true;
    }

    override _unfocus() {
        const s = this._s;
        this.h = ITEM_H;
        s.tag('FocusBg').visible = false;
        s.tag('Icon').patch({ w: 102, h: 102, x: 76, y: 0, color: 0x80ffffff });
        s.tag('Icon.Logo').patch({ w: 82, h: 82 });
        s.tag('ChannelNumber').patch({ visible: true, x: 32, y: 51, text: { fontWeight: 500, textColor: 0x80ffffff, fontFace: Helper.getFontFamily('Regular') } });
        s.tag('ChannelName').patch({ visible: false, y: 10, text: CHANNEL_NAME_TEXT_UNFOCUSED });
        s.tag('ProgramName').visible = true;
        s.tag('ProgressTrack').patch({ visible: true, y: PROGRESS_Y });
        s.tag('NowPlayingWrapper').patch({ visible: false, y: PROGRESS_Y });
        // Do NOT fire $itemChanged here — _focus() on the incoming item fires it once
        // with both height changes already applied, giving the Column a single correct
        // layout pass and preventing a visible intermediate scroll position.
    }
}
